/**
 * Simulador de Taxas — calcula taxa de cartão de crédito/débito.
 *
 * Duas abas:
 *   1) "Quanto vou receber" → dado valor da venda, mostra líquido / desconto.
 *   2) "Quanto preciso cobrar" → dado líquido desejado, mostra o gross-up.
 *
 * O cadastro de máquinas armazena 2 taxas por slot (descontada + repassada).
 * O usuário escolhe ao cadastrar qual ele vai digitar — o sistema calcula a
 * outra automaticamente usando:
 *   taxa_repasse  = (1 / (1 - desconto)) - 1
 *   taxa_desconto = 1 - (1 / (1 + repasse))
 * O botão "Editar manualmente" desliga o auto-cálculo.
 *
 * Tabela de parcelas: mostra todas as 1..24 parcelas com a taxa, o que o
 * cliente paga e o que a loja recebe — útil para apresentar opções no balcão.
 */
import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Calculator, Share2, ImageDown, Power, Save, Repeat, History, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/AuthContext";
import { toPng } from "html-to-image";

const INSTALL_KEYS = Array.from({ length: 24 }, (_, i) => String(i + 1));
const blankRate = () => ({ discounted: 0, passed: 0 });
const blankMachine = () => ({
  name: "",
  observations: "",
  photo: "",
  status: "active",
  fee_input_type: "discounted",
  debit: blankRate(),
  credit: Object.fromEntries(INSTALL_KEYS.map(k => [k, blankRate()])),
});

// Cálculo de contrapartida (taxa descontada ↔ taxa repassada).
const pctToRatio = (p) => (Number(p) || 0) / 100;
const ratioToPct = (r) => Number(r) * 100;
const calcPassedFromDiscounted = (discountedPct) => {
  const d = pctToRatio(discountedPct);
  if (d >= 1) return 0;
  return ratioToPct(1 / (1 - d) - 1);
};
const calcDiscountedFromPassed = (passedPct) => {
  const p = pctToRatio(passedPct);
  if (p <= -1) return 0;
  return ratioToPct(1 - 1 / (1 + p));
};

// Lê o slot da máquina para um determinado meio de pagamento (debit ou credit/Nx).
const getRate = (machine, method, parcels) => {
  if (!machine) return blankRate();
  if (method === "debit") return machine.debit || blankRate();
  return (machine.credit || {})[String(parcels)] || blankRate();
};

// Formato pt-BR para porcentagem com 2-4 dígitos, sem zeros à direita supérfluos.
const pctFmt = (v) => `${(Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`;

export default function SimuladorTaxas() {
  const { user } = useAuth();
  const [machines, setMachines] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Form da aba
  // OBS: "charge" (Receber sem desconto) é o DEFAULT — o vendedor normalmente
  //      quer saber quanto cobrar do cliente para receber o valor inteiro.
  const [tab, setTab] = useState("charge");
  const [paymentMethod, setPaymentMethod] = useState("credit");
  const [installments, setInstallments] = useState(1);
  const [valueInput, setValueInput] = useState("");
  const [customerName, setCustomerName] = useState("");

  // Dialogs
  const [editing, setEditing] = useState(null); // máquina sendo cadastrada/editada
  const [manualMode, setManualMode] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const resultRef = useRef(null);
  // Ref específico para exportar APENAS a tabela de parcelas (sem o card de resultado).
  // O usuário pediu para a foto conter somente a tabela completa.
  const tableRef = useRef(null);

  const reload = async () => {
    try {
      const [m, h] = await Promise.all([
        api.get("/fee-machines"),
        api.get("/fee-simulations"),
      ]);
      setMachines(m.data || []);
      setHistory(h.data || []);
      if (!selectedId && (m.data || []).length > 0) {
        const active = m.data.find(x => x.status === "active") || m.data[0];
        setSelectedId(active.id);
      }
    } catch (e) {
      toast.error("Erro ao carregar máquinas");
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const selectedMachine = useMemo(() => machines.find(m => m.id === selectedId) || null, [machines, selectedId]);

  // ─── Cálculo da simulação ────────────────────────────────────────────
  const numericInput = useMemo(() => {
    const v = (valueInput || "").toString().replace(/\./g, "").replace(",", ".").replace(/[^\d.]/g, "");
    return Number(v) || 0;
  }, [valueInput]);

  const result = useMemo(() => {
    const rate = getRate(selectedMachine, paymentMethod, installments);
    const dPct = Number(rate.discounted) || 0;
    const pPct = Number(rate.passed) || 0;
    if (tab === "receive") {
      // input = quanto o cliente paga; loja recebe = input * (1 - dPct/100)
      const discount = numericInput * pctToRatio(dPct);
      const net = Math.max(0, numericInput - discount);
      return { rate, dPct, pPct, discount, net, charge: numericInput };
    } else {
      // input = quanto a loja quer receber; cliente paga = input * (1 + pPct/100)
      const surcharge = numericInput * pctToRatio(pPct);
      const charge = numericInput + surcharge;
      return { rate, dPct, pPct, surcharge, charge, net: numericInput };
    }
  }, [selectedMachine, paymentMethod, installments, numericInput, tab]);

  const allInstallmentsTable = useMemo(() => {
    if (!selectedMachine) return [];
    const rows = [];
    const debit = selectedMachine.debit || blankRate();
    const debitHas = (Number(debit.discounted) || 0) > 0 || (Number(debit.passed) || 0) > 0;
    if (debitHas) rows.push({ key: "debit", method: "debit", parcels: 1, label: "Débito", rate: debit });
    INSTALL_KEYS.forEach(k => {
      const r = (selectedMachine.credit || {})[k] || blankRate();
      const has = (Number(r.discounted) || 0) > 0 || (Number(r.passed) || 0) > 0;
      if (has) rows.push({ key: `credit_${k}`, method: "credit", parcels: Number(k), label: `${k}x`, rate: r });
    });
    return rows;
  }, [selectedMachine]);

  // ─── Salvar simulação manualmente ───────────────────────────────────
  const saveSimulation = async () => {
    if (!selectedMachine) return toast.error("Selecione uma máquina");
    if (!numericInput) return toast.error("Informe o valor");
    try {
      const payload = {
        machine_id: selectedMachine.id,
        machine_name: selectedMachine.name,
        sim_type: tab,
        payment_method: paymentMethod,
        installments: paymentMethod === "debit" ? 1 : installments,
        customer_name: customerName,
        input_amount: numericInput,
        applied_rate_discounted: result.dPct,
        applied_rate_passed: result.pPct,
        net_amount: result.net || 0,
        charge_amount: result.charge || 0,
      };
      await api.post("/fee-simulations", payload);
      toast.success("Simulação salva no histórico");
      reload();
    } catch { toast.error("Erro ao salvar simulação"); }
  };

  // ─── Compartilhar via WhatsApp (texto formatado) ────────────────────
  const shareWhatsApp = () => {
    if (!selectedMachine || !numericInput) return toast.error("Informe o valor primeiro");
    const lines = [];
    lines.push(`*Simulação de pagamento*`);
    if (customerName) lines.push(`Cliente: ${customerName}`);
    lines.push(`Maquininha: ${selectedMachine.name}`);
    lines.push(`Parcelamento: ${paymentMethod === "debit" ? "Débito" : `${installments}x`}`);
    if (tab === "receive") {
      lines.push(`Valor da venda: *${brl(result.charge)}*`);
      lines.push(`Taxa: ${pctFmt(result.dPct)}`);
      lines.push(`Desconto: ${brl(result.discount)}`);
      lines.push(`Líquido recebido: *${brl(result.net)}*`);
    } else {
      lines.push(`Valor líquido desejado: *${brl(result.net)}*`);
      lines.push(`Taxa repassada: ${pctFmt(result.pPct)}`);
      lines.push(`Acréscimo: ${brl(result.surcharge)}`);
      lines.push(`Cliente paga: *${brl(result.charge)}*`);
    }
    // Lista 1x..N quando crédito: cada linha mostra parcela e valor recalculado
    // com a taxa daquela faixa específica. Útil para o vendedor enviar a tabela
    // resumida das opções até o parcelamento selecionado.
    if (paymentMethod === "credit") {
      const rowsForShare = allInstallmentsTable.filter(r => r.method === "credit" && r.parcels <= installments);
      if (rowsForShare.length > 0) {
        lines.push(`-`);
        rowsForShare.forEach(r => {
          const pPct = Number(r.rate.passed) || 0;
          const dPct = Number(r.rate.discounted) || 0;
          const inputAmt = numericInput || 0;
          const totalCharge = tab === "receive" ? inputAmt : inputAmt * (1 + pctToRatio(pPct));
          const _ = dPct; // mantém o lint feliz; usado na tabela mas não no share resumido
          const parcelValue = totalCharge / r.parcels;
          lines.push(`${r.parcels}x ${brl(parcelValue)}`);
        });
      }
    }
    const url = `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener");
  };

  // ─── Salvar como imagem (PNG → Galeria via Web Share API ou download) ─
  // Captura APENAS a tabela de parcelas, conforme pedido pelo usuário.
  const saveAsImage = async () => {
    if (!tableRef.current) return;
    try {
      const dataUrl = await toPng(tableRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#ffffff" });
      const blob = await (await fetch(dataUrl)).blob();
      const fileName = `simulacao-${Date.now()}.png`;
      const file = new File([blob], fileName, { type: "image/png" });
      // iOS/Android: tenta Web Share API (oferece salvar na galeria, AirDrop, etc.)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Simulação de taxa" });
        return;
      }
      // Fallback: download direto
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error("Não foi possível gerar a imagem");
    }
  };

  // ─── Editor de máquina ──────────────────────────────────────────────
  const openEditor = (m) => setEditing(JSON.parse(JSON.stringify(m || blankMachine())));
  const submitEditor = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await api.put(`/fee-machines/${editing.id}`, editing);
      } else {
        await api.post("/fee-machines", editing);
      }
      toast.success("Máquina salva");
      setEditing(null);
      setManualMode(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar máquina");
    }
  };
  const deleteMachine = async (mid) => {
    if (!window.confirm("Excluir esta máquina?")) return;
    try {
      await api.delete(`/fee-machines/${mid}`);
      toast.success("Máquina excluída");
      if (selectedId === mid) setSelectedId(null);
      reload();
    } catch { toast.error("Erro ao excluir"); }
  };

  // Atualiza taxa no editor, com auto-cálculo da contrapartida quando NÃO em modo manual.
  const updateRate = (slotKey, value) => {
    if (!editing) return;
    const num = Number(String(value).replace(",", ".")) || 0;
    const isDisc = editing.fee_input_type === "discounted";
    const otherKey = isDisc ? "passed" : "discounted";
    const otherVal = manualMode ? undefined : (isDisc ? calcPassedFromDiscounted(num) : calcDiscountedFromPassed(num));
    setEditing(prev => {
      const next = { ...prev };
      if (slotKey === "debit") {
        next.debit = { ...next.debit, [isDisc ? "discounted" : "passed"]: num };
        if (!manualMode) next.debit[otherKey] = otherVal;
      } else {
        const credit = { ...(next.credit || {}) };
        const slot = { ...(credit[slotKey] || blankRate()) };
        slot[isDisc ? "discounted" : "passed"] = num;
        if (!manualMode) slot[otherKey] = otherVal;
        credit[slotKey] = slot;
        next.credit = credit;
      }
      return next;
    });
  };
  const updateRateOther = (slotKey, value) => {
    // Edição manual do CAMPO contraparte (só no modo manual)
    if (!editing || !manualMode) return;
    const num = Number(String(value).replace(",", ".")) || 0;
    const isDisc = editing.fee_input_type === "discounted";
    const otherKey = isDisc ? "passed" : "discounted";
    setEditing(prev => {
      const next = { ...prev };
      if (slotKey === "debit") {
        next.debit = { ...next.debit, [otherKey]: num };
      } else {
        const credit = { ...(next.credit || {}) };
        const slot = { ...(credit[slotKey] || blankRate()) };
        slot[otherKey] = num;
        credit[slotKey] = slot;
        next.credit = credit;
      }
      return next;
    });
  };

  // Upload de foto (max 1MB pra evitar payloads pesados)
  const onPhotoUpload = (file) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) return toast.error("Imagem muito grande (máx 1.5MB)");
    const reader = new FileReader();
    reader.onload = () => setEditing(prev => ({ ...prev, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const repeatSimulation = (s) => {
    setSelectedId(s.machine_id);
    setTab(s.sim_type);
    setPaymentMethod(s.payment_method);
    setInstallments(s.installments || 1);
    setValueInput(String(s.input_amount).replace(".", ","));
    setCustomerName(s.customer_name || "");
    setShowHistory(false);
    toast.success("Simulação carregada");
  };
  const deleteSim = async (sid) => {
    if (!window.confirm("Excluir simulação do histórico?")) return;
    try { await api.delete(`/fee-simulations/${sid}`); reload(); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-5 px-1 sm:px-0" data-testid="simulador-taxas-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Simulador de Taxas</h1>
          <p className="text-sm text-neutral-500">Calcule taxas de cartão e gere propostas instantâneas para seus clientes.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHistory(true)} data-testid="open-history-btn">
            <History size={14} className="mr-1.5"/>Histórico
          </Button>
          <Button onClick={() => openEditor(null)} className="bg-[#0A0A0A] hover:bg-black text-white" size="sm" data-testid="new-machine-btn">
            <Plus size={14} className="mr-1.5"/>Nova máquina
          </Button>
        </div>
      </div>

      {/* Lista de máquinas (chips clicáveis) */}
      {machines.length === 0 ? (
        <Card className="p-8 text-center border-dashed">
          <p className="text-sm text-neutral-500 mb-3">Você ainda não cadastrou nenhuma máquina.</p>
          <Button onClick={() => openEditor(null)} className="bg-[#0A0A0A] hover:bg-black text-white">
            <Plus size={14} className="mr-1.5"/>Cadastrar primeira máquina
          </Button>
        </Card>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="machines-list">
          {machines.map(m => (
            <div key={m.id} className={`group flex items-center gap-2 rounded-full border pl-3 pr-1 py-1 transition ${selectedId === m.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white dark:bg-white/5 hover:border-neutral-400"}`}>
              <button onClick={() => setSelectedId(m.id)} className="text-sm font-medium" data-testid={`machine-chip-${m.id}`}>
                {m.name}
                {m.status === "inactive" && <span className="ml-1 text-[10px] opacity-70">(inativa)</span>}
              </button>
              <Button size="icon" variant={selectedId === m.id ? "secondary" : "ghost"} className="h-6 w-6 rounded-full" onClick={() => openEditor(m)} title="Editar">
                <Pencil size={11}/>
              </Button>
              <Button size="icon" variant={selectedId === m.id ? "secondary" : "ghost"} className="h-6 w-6 rounded-full" onClick={() => deleteMachine(m.id)} title="Excluir">
                <Trash2 size={11}/>
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Simulador */}
      {selectedMachine && (
        <Card className="p-4 sm:p-6 space-y-4 border-[#E5E5E5]">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full max-w-md">
              <TabsTrigger value="charge" data-testid="tab-charge">Receber sem desconto</TabsTrigger>
              <TabsTrigger value="receive" data-testid="tab-receive">Receber com desconto</TabsTrigger>
            </TabsList>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Forma de pagamento</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger data-testid="payment-method"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Débito</SelectItem>
                    <SelectItem value="credit">Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Parcelamento</Label>
                <Select value={String(installments)} onValueChange={(v)=>setInstallments(Number(v))} disabled={paymentMethod === "debit"}>
                  <SelectTrigger data-testid="installments-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {INSTALL_KEYS.slice(0, 24).map(k => <SelectItem key={k} value={k}>{k}x</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Cliente (opcional)</Label>
                <Input value={customerName} onChange={e=>setCustomerName(e.target.value)} placeholder="Nome para a proposta" data-testid="customer-name-input"/>
              </div>
            </div>

            <TabsContent value="receive" className="mt-4">
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Valor da venda</Label>
              <Input
                value={valueInput}
                onChange={e=>setValueInput(e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
                className="text-2xl font-display h-14"
                data-testid="value-input-receive"
              />
            </TabsContent>
            <TabsContent value="charge" className="mt-4">
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Quanto quero receber líquido</Label>
              <Input
                value={valueInput}
                onChange={e=>setValueInput(e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
                className="text-2xl font-display h-14"
                data-testid="value-input-charge"
              />
            </TabsContent>
          </Tabs>

          {/* RESULTADO — região capturada pela imagem */}
          <div ref={resultRef} className="bg-white dark:bg-neutral-900 rounded-xl border border-[#E5E5E5] p-5 space-y-4" data-testid="result-card">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div className="font-display text-lg">{selectedMachine.name}</div>
              <div className="text-xs text-neutral-500">{paymentMethod === "debit" ? "Débito" : `${installments}x`} • Taxa {pctFmt(tab === "receive" ? result.dPct : result.pPct)}</div>
            </div>
            {customerName && <div className="text-sm text-neutral-700 dark:text-neutral-300">Cliente: <strong>{customerName}</strong></div>}

            {tab === "receive" ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-500/10 dark:border-blue-500/30 p-4">
                  <div className="text-[11px] uppercase text-blue-700 dark:text-blue-300">Cliente paga</div>
                  <div className="font-display text-2xl text-blue-700 dark:text-blue-200" data-testid="result-charge">{brl(result.charge)}</div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30 p-4">
                  <div className="text-[11px] uppercase text-red-700 dark:text-red-300">Desconto da operadora</div>
                  <div className="font-display text-2xl text-red-700 dark:text-red-200" data-testid="result-discount">−{brl(result.discount)}</div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/30 p-4">
                  <div className="text-[11px] uppercase text-emerald-700 dark:text-emerald-300">Loja recebe</div>
                  <div className="font-display text-2xl text-emerald-700 dark:text-emerald-200" data-testid="result-net">{brl(result.net)}</div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/30 p-4">
                  <div className="text-[11px] uppercase text-emerald-700 dark:text-emerald-300">Quero receber</div>
                  <div className="font-display text-2xl text-emerald-700 dark:text-emerald-200" data-testid="result-net-charge">{brl(result.net)}</div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 p-4">
                  <div className="text-[11px] uppercase text-amber-700 dark:text-amber-300">Acréscimo</div>
                  <div className="font-display text-2xl text-amber-700 dark:text-amber-200" data-testid="result-surcharge">+{brl(result.surcharge)}</div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-500/10 dark:border-blue-500/30 p-4">
                  <div className="text-[11px] uppercase text-blue-700 dark:text-blue-300">Cliente paga</div>
                  <div className="font-display text-2xl text-blue-700 dark:text-blue-200" data-testid="result-charge2">{brl(result.charge)}</div>
                </div>
              </div>
            )}
            <p className="text-[11px] text-neutral-500">Simulação informativa. Confira sempre os valores no extrato da operadora.</p>
          </div>

          {/* Ações */}
          <div className="flex flex-wrap gap-2">
            <Button onClick={saveSimulation} variant="outline" size="sm" data-testid="save-sim-btn">
              <Save size={14} className="mr-1.5"/>Salvar simulação
            </Button>
            <Button onClick={saveAsImage} variant="outline" size="sm" data-testid="save-image-btn">
              <ImageDown size={14} className="mr-1.5"/>Salvar como imagem
            </Button>
            <Button onClick={shareWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="share-whatsapp-btn">
              <Share2 size={14} className="mr-1.5"/>WhatsApp
            </Button>
          </div>

          {/* Tabela INLINE — todas as parcelas cadastradas (oculta as zeradas). A
              linha correspondente ao parcelamento selecionado fica DESTACADA.
              `tableRef` envolve TODA essa região, pois é o que vai para a foto. */}
          {allInstallmentsTable.length > 0 && (
            <div ref={tableRef} className="pt-2 bg-white dark:bg-neutral-900 rounded-lg p-3">
              <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
                <div>
                  <div className="font-display text-lg">{selectedMachine.name}</div>
                  {customerName && <div className="text-xs text-neutral-500">Cliente: {customerName}</div>}
                </div>
                <div className="text-xs text-neutral-500">
                  {tab === "receive" ? "Valor da venda" : "Quero receber"}: <strong className="text-neutral-700 dark:text-neutral-200">{brl(numericInput)}</strong>
                </div>
              </div>
              <div className="overflow-x-auto rounded-md border border-[#E5E5E5]">
                <table className="w-full text-sm" data-testid="all-installments-inline">
                  <thead className="bg-neutral-50 dark:bg-white/5 border-b border-[#E5E5E5] text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Parcela</th>
                      <th className="px-3 py-2 font-medium">Taxa desc.</th>
                      <th className="px-3 py-2 font-medium">Taxa repasse</th>
                      <th className="px-3 py-2 font-medium text-right">Cliente paga</th>
                      <th className="px-3 py-2 font-medium text-right">Loja recebe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allInstallmentsTable.map(row => {
                      const dPct = Number(row.rate.discounted) || 0;
                      const pPct = Number(row.rate.passed) || 0;
                      const inputAmt = numericInput || 0;
                      const charge = tab === "receive" ? inputAmt : inputAmt * (1 + pctToRatio(pPct));
                      const net = tab === "receive" ? inputAmt * (1 - pctToRatio(dPct)) : inputAmt;
                      const parcelValue = row.method === "debit" ? charge : charge / row.parcels;
                      const isSelected = row.method === paymentMethod && (paymentMethod === "debit" || row.parcels === installments);
                      return (
                        <tr
                          key={row.key}
                          className={`border-b border-[#E5E5E5] cursor-pointer transition ${isSelected ? "bg-amber-50 dark:bg-amber-500/15 ring-1 ring-inset ring-amber-300" : "hover:bg-neutral-50 dark:hover:bg-white/5"}`}
                          onClick={() => { setPaymentMethod(row.method); if (row.method === "credit") setInstallments(row.parcels); }}
                          data-testid={`installments-row-${row.key}`}
                        >
                          <td className="px-3 py-2 font-medium whitespace-nowrap">
                            <span>{row.label}</span>
                            {row.method === "credit" && (
                              <span className="ml-1 text-neutral-600 dark:text-neutral-300 font-normal">{brl(parcelValue)}</span>
                            )}
                            {isSelected && <span className="ml-2 text-[10px] uppercase font-bold text-amber-700 dark:text-amber-300">selecionado</span>}
                          </td>
                          <td className="px-3 py-2 text-xs">{pctFmt(dPct)}</td>
                          <td className="px-3 py-2 text-xs">{pctFmt(pPct)}</td>
                          <td className="px-3 py-2 text-right text-blue-700 dark:text-blue-300">{brl(charge)}</td>
                          <td className="px-3 py-2 text-right text-emerald-700 dark:text-emerald-300">{brl(net)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-neutral-500 mt-2">Clique em uma linha para selecionar essa condição automaticamente.</p>
            </div>
          )}
        </Card>
      )}

      {/* (Dialog "todas as parcelas" removido — agora é inline acima) */}

      {/* DIALOG: Histórico */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader><DialogTitle>Histórico de simulações</DialogTitle></DialogHeader>
          {history.length === 0 ? (
            <p className="text-sm text-neutral-500 text-center py-8">Nenhuma simulação salva ainda.</p>
          ) : (
            <div className="space-y-2">
              {history.map(s => (
                <div key={s.id} className="border border-[#E5E5E5] rounded-lg p-3 flex flex-wrap items-start gap-3" data-testid={`history-item-${s.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-xs text-neutral-500">{new Date(s.created_at).toLocaleString('pt-BR')} • {s.user_name}</div>
                    <div className="text-sm font-medium">
                      {s.machine_name} • {s.payment_method === "debit" ? "Débito" : `${s.installments}x`}
                      {s.customer_name && <span className="text-neutral-500"> • {s.customer_name}</span>}
                    </div>
                    <div className="text-xs text-neutral-600 dark:text-neutral-400">
                      {s.sim_type === "receive"
                        ? <>Venda {brl(s.input_amount)} → líquido <strong>{brl(s.net_amount)}</strong></>
                        : <>Líquido {brl(s.input_amount)} → cobrar <strong>{brl(s.charge_amount)}</strong></>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => repeatSimulation(s)} title="Repetir">
                      <Repeat size={14}/>
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => deleteSim(s.id)} title="Excluir">
                      <Trash2 size={14}/>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DIALOG: Editor de máquina */}
      <Dialog open={!!editing} onOpenChange={(o)=>{ if(!o){setEditing(null); setManualMode(false);} }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="machine-editor-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Editar máquina" : "Nova máquina"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nome da máquina</Label>
                  <Input value={editing.name} onChange={e=>setEditing({...editing, name: e.target.value})} placeholder="Stone, Mercado Pago, etc." data-testid="machine-name-input"/>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    Status
                    <Switch checked={editing.status === "active"} onCheckedChange={(v)=>setEditing({...editing, status: v ? "active" : "inactive"})}/>
                    <span className="text-xs text-neutral-500">{editing.status === "active" ? "Ativa" : "Inativa"}</span>
                  </Label>
                </div>
              </div>

              <div>
                <Label>Observações (opcional)</Label>
                <Input value={editing.observations} onChange={e=>setEditing({...editing, observations: e.target.value})} placeholder="Aluguel, condições especiais..."/>
              </div>

              <div>
                <Label>Foto do contrato/comprovante (opcional)</Label>
                <Input type="file" accept="image/*" onChange={(e)=>onPhotoUpload(e.target.files?.[0])} data-testid="machine-photo-input"/>
                {editing.photo && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={editing.photo} alt="" className="h-20 w-20 object-cover rounded border"/>
                    <Button size="sm" variant="ghost" onClick={()=>setEditing({...editing, photo: ""})}>Remover</Button>
                  </div>
                )}
              </div>

              <div className="border-t pt-3 space-y-3">
                <Label>Tipo de taxa cadastrada</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button" size="sm"
                    variant={editing.fee_input_type === "discounted" ? "default" : "outline"}
                    onClick={() => setEditing({ ...editing, fee_input_type: "discounted" })}
                    className={editing.fee_input_type === "discounted" ? "bg-[#0A0A0A] hover:bg-black text-white" : ""}
                    data-testid="fee-type-discounted"
                  >Taxa descontada pela operadora</Button>
                  <Button
                    type="button" size="sm"
                    variant={editing.fee_input_type === "passed" ? "default" : "outline"}
                    onClick={() => setEditing({ ...editing, fee_input_type: "passed" })}
                    className={editing.fee_input_type === "passed" ? "bg-[#0A0A0A] hover:bg-black text-white" : ""}
                    data-testid="fee-type-passed"
                  >Taxa repassada ao cliente (gross-up)</Button>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={manualMode} onCheckedChange={setManualMode}/>
                  <span className="text-sm">Editar taxas manualmente (sem auto-cálculo)</span>
                </div>
              </div>

              <FeeTableEditor editing={editing} manualMode={manualMode} updateRate={updateRate} updateRateOther={updateRateOther}/>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>{ setEditing(null); setManualMode(false); }}>Cancelar</Button>
            <Button onClick={submitEditor} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="save-machine-btn">Salvar máquina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function FeeTableEditor({ editing, manualMode, updateRate, updateRateOther }) {
  const isDisc = editing.fee_input_type === "discounted";
  const primaryLabel = isDisc ? "Taxa descontada (%)" : "Taxa repassada (%)";
  const otherLabel = isDisc ? "Taxa repassada (%)" : "Taxa descontada (%)";
  const slots = [
    { key: "debit", label: "Débito" },
    ...INSTALL_KEYS.map(k => ({ key: k, label: `${k}x` })),
  ];
  const valFor = (slotKey, which) => {
    const r = slotKey === "debit" ? editing.debit : (editing.credit || {})[slotKey] || blankRate();
    return r[which] || 0;
  };
  return (
    <div className="border-t pt-3">
      <Label className="text-sm font-semibold">Tabela de taxas</Label>
      <div className="text-xs text-neutral-500 mb-2">Preencha apenas a coluna principal. {!manualMode && "A contrapartida é calculada automaticamente."}</div>
      <div className="overflow-x-auto rounded-md border border-[#E5E5E5]">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-white/5">
            <tr>
              <th className="px-2 py-2 text-left font-medium">Parcela</th>
              <th className="px-2 py-2 text-left font-medium">{primaryLabel}</th>
              <th className="px-2 py-2 text-left font-medium text-neutral-500">{otherLabel}</th>
            </tr>
          </thead>
          <tbody>
            {slots.map(s => {
              const primaryKey = isDisc ? "discounted" : "passed";
              const otherKey = isDisc ? "passed" : "discounted";
              return (
                <tr key={s.key} className="border-t border-[#E5E5E5]">
                  <td className="px-2 py-1 font-medium">{s.label}</td>
                  <td className="px-2 py-1">
                    <Input
                      value={valFor(s.key, primaryKey) === 0 ? "" : valFor(s.key, primaryKey)}
                      onChange={(e)=>updateRate(s.key, e.target.value)}
                      type="number" step="0.01" min="0"
                      placeholder="0"
                      className="h-8"
                      data-testid={`rate-input-${s.key}`}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      value={(() => {
                        const v = valFor(s.key, otherKey);
                        if (!v) return "";
                        return Number(v).toFixed(4).replace(/\.?0+$/, "");
                      })()}
                      onChange={(e)=>updateRateOther(s.key, e.target.value)}
                      type="number" step="0.0001" min="0"
                      placeholder="0"
                      disabled={!manualMode}
                      className="h-8 bg-neutral-50 dark:bg-white/5"
                      data-testid={`rate-other-${s.key}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
